export const performanceEn = {
  title: 'Performance',
  subtitle: 'Goal Setting, Evaluation & Development',

  // Navigation
  nav: {
    admin: 'Admin',
  },

  // Organization selection
  organization: {
    select: 'Select Organization',
    current: 'Current',
    loading: 'Loading...',
    all: 'All Organizations',
  },

  // Admin Setup Overview (cycle-centric)
  adminOverview: {
    title: 'Setup',
    // Empty state
    noCycle: 'No Performance Cycle',
    noCycleDesc: 'Create your first performance cycle to start managing team performance',
    createCycle: 'Create Cycle',
    // Cycle info
    allCycles: 'All Cycles',
    // Stats cards
    kpiProgress: 'KPI Evaluation',
    e360Progress: '360 Evaluation',
    calibration: 'Calibration',
    confirmed: 'Confirmed',
    completed: 'Completed',
    inProgress: 'In Progress',
    done: 'Done',
    notStarted: 'Not Started',
    // Quick actions
    quickActions: 'Quick Actions',
    manageKpi: 'Manage KPI Evaluation',
    manageCalibration: 'Manage Calibration Sessions',
    publishResults: 'Publish Results',
    // Stage hints
    stageHints: {
      draft: {
        title: 'Draft Stage Setup',
        desc: 'Please select a grade configuration for this cycle',
        noConfigs: 'No grade configurations yet, please create one',
        createConfig: 'Create Config',
        selectLabel: 'Select Grade Config',
        selectPlaceholder: '-- Select --',
        default: 'Default',
        manageConfigs: 'Manage Configs',
        apply: 'Apply to Cycle',
        applied: 'Applied',
        advance: 'Publish Cycle',
        advanceConfirm: 'Publish this cycle and start goal setting?',
      },
      goalSetting: {
        title: 'Goal Setting Stage',
        desc: 'Employees are setting KPI targets via free-form input',
        action: 'View KPI Settings',
        advance: 'Start Execution',
        advanceConfirm: 'End goal setting and start execution?',
      },
      inProgress: {
        title: 'Execution Stage',
        desc: 'The cycle is in execution, employees are working on their goals',
        action: 'View Progress',
        advance: 'Start Evaluation',
        advanceConfirm: 'End execution and start the evaluation phase?',
      },
      evaluating: {
        title: 'Evaluation Stage',
        desc: 'Employees are completing self and peer evaluations',
        action: 'View Evaluation Progress',
        advance: 'Start Calibration',
        advanceConfirm: 'End evaluation and start calibration?',
      },
      calibrating: {
        title: 'Calibration Stage',
        desc: 'Organize calibration sessions to ensure fair ratings',
        action: 'Manage Calibration',
        advance: 'Publish Results',
        advanceConfirm: 'Complete calibration and publish results? Employees will be notified.',
      },
      confirming: {
        title: 'Result Confirmation Stage',
        desc: 'Performance results have been published. Waiting for employees to review and confirm.',
        action: 'View Confirmation Progress',
        advance: 'Complete Cycle',
        advanceConfirm: 'Complete this cycle? Unconfirmed employees will be treated as accepted.',
      },
      completed: {
        title: 'Cycle Completed',
        desc: 'All employees have confirmed their results. You can archive when ready.',
        advance: 'Archive Cycle',
        advanceConfirm: 'Archive this cycle? Archived cycles are read-only.',
      },
      advancing: 'Processing...',
    },
    goalProgress: {
      title: 'Goal Setting Progress',
      submitted: 'submitted',
      submittedLabel: 'Submitted',
      draftLabel: 'In Draft',
      notStartedLabel: 'Not Started',
      deptProgress: 'Department Progress',
      deptName: 'Department',
      deptProgressLabel: 'Progress',
      deptSubmitted: 'submitted',
      deptPending: 'pending',
      pendingTitle: 'Pending Employees',
      kpiCount: 'KPIs',
      statusDraft: 'Draft',
      statusNotStarted: 'Not Started',
      showAll: 'View all',
      people: 'people',
      collapse: 'Collapse',
    },
    evalProgress: {
      title: 'Evaluation Progress',
      selfEval: 'Self',
      managerEval: 'Manager',
      selfCompleted: 'self-evaluated',
      managerCompleted: 'manager-evaluated',
      completedLabel: 'Completed',
      pendingLabel: 'Pending',
      deptProgress: 'Department Progress',
      pendingTitle: 'Pending Evaluations',
      e360Eval: '360 Eval',
      e360Completed: 'completed',
      pendingSelf: 'Self eval pending',
      pendingManager: 'Manager eval pending',
      pendingBoth: 'Both pending',
      pending360: '360 pending',
      showAll: 'View all',
      people: 'people',
      collapse: 'Collapse',
    },
    calProgress: {
      title: 'Calibration Progress',
      calibrated: 'calibrated',
      gradeDistribution: 'Grade Distribution',
      deptProgress: 'Department Calibration',
      deptCalibrated: 'calibrated',
      pendingTitle: 'Pending Calibration',
      avgScore: 'Avg',
      showAll: 'View all',
      people: 'people',
      collapse: 'Collapse',
    },
    confirmProgress: {
      title: 'Result Confirmation Progress',
      confirmed: 'confirmed',
      confirmedLabel: 'Confirmed',
      pendingLabel: 'Pending',
      appealedLabel: 'Appealed',
      deptProgress: 'Department Confirmation',
      deptConfirmed: 'Confirmed',
      pendingTitle: 'Pending Confirmation',
      showAll: 'View All',
      people: 'people',
      collapse: 'Collapse',
    },
    resultsSummary: {
      title: 'Performance Results',
      orgAvg: 'Org Average',
      gradeDistribution: 'Grade Distribution',
      deptComparison: 'Department Comparison',
      deptConfirmProgress: 'Department Confirmation Progress',
      deptAvg: 'Avg',
      completionTitle: 'Completion',
      selfEval: 'Self Eval',
      managerEval: 'Manager Eval',
      e360: '360 Eval',
      calibration: 'Calibration',
    },
  },

  // Overview page
  overview: {
    title: 'Overview',
    loading: 'Loading...',
    currentCycle: 'Current Cycle',
    noCycle: 'No Active Performance Cycle',
    noCycleDesc: 'Please contact HR to create a new performance cycle',
    cycleProgress: 'Cycle Progress',
    myTasks: 'My Tasks',
    noTasks: 'No Pending Tasks',
    noTasksDesc: 'All tasks completed',
    quickActions: 'Quick Actions',
    dueDate: 'Due Date',
    taskKpiSetup: 'Set Up KPI Goals',
    taskKpiSetupDesc: 'No KPIs created yet',
    taskKpiIncomplete: '{count} KPIs need completion',
    taskSelfEval: 'Complete Self-Evaluation',
    taskSelfEvalDesc: 'Evaluate your KPIs for this cycle',
    taskE360Config: 'Configure 360 Evaluators',
    taskE360ConfigDesc: 'Select colleagues for 360 feedback',
    taskE360Feedback: 'Complete 360 Feedback',
    taskE360FeedbackWithName: 'Complete 360 feedback for {name}',
    taskE360FeedbackDue: 'Due {date}',
    taskColleague: 'Colleague',
    mockCycleName: '2025 Annual Performance Cycle',
  },

  // Module entries
  modules: {
    kpi: {
      title: 'My KPI',
      desc: 'View KPI indicators and evaluation progress',
    },
    e360: {
      title: '360 Feedback',
      desc: 'Multi-dimensional feedback & evaluation tasks',
    },
    result: {
      title: 'My Performance',
      desc: 'View historical performance results',
    },
    team: {
      title: 'Team Performance',
      desc: 'Manage team goals and evaluations',
    },
  },

  // Cycle management
  cycle: {
    title: 'Cycle Management',
    list: 'Cycle List',
    create: 'Create Cycle',
    emptyTitle: 'No cycles yet',
    emptyDesc: 'Create a performance cycle to get started',
    emptyAction: 'Create first cycle',
    name: 'Cycle Name',
    organization: 'Organization',
    type: 'Cycle Type',
    status: 'Status',
    startDate: 'Start Date',
    endDate: 'End Date',
    actions: {
      publish: 'Publish Cycle',
      startExecution: 'Start Execution',
      startEvaluation: 'Start Evaluation',
      startCalibration: 'Start Calibration',
      publishResults: 'Publish Results',
      complete: 'Complete Cycle',
      archive: 'Archive',
      createChild: 'Create Sub-cycle',
    },
    steps: {
      draft: 'Draft',
      goalSetting: 'Goal Setting',
      inProgress: 'In Progress',
      evaluating: 'Evaluating',
      calibrating: 'Calibrating',
      confirming: 'Confirming',
      completed: 'Completed',
      archived: 'Archived',
    },
    types: {
      ANNUAL: 'Annual',
      SEMI_ANNUAL: 'Semi-Annual',
      QUARTERLY: 'Quarterly',
      MONTHLY: 'Monthly',
    },
    statuses: {
      DRAFT: 'Draft',
      GOAL_SETTING: 'Goal Setting',
      IN_PROGRESS: 'In Progress',
      EVALUATING: 'Evaluating',
      CALIBRATING: 'Calibrating',
      CONFIRMING: 'Confirming',
      COMPLETED: 'Completed',
      ARCHIVED: 'Archived',
    },
    detail: {
      notFound: 'Cycle Not Found',
      progressTitle: 'Cycle Progress',
      infoTitle: 'Basic Info',
      statsTitle: 'Statistics',
      shortcutsTitle: 'Quick Links',
      typeLabel: 'Cycle Type',
      statusLabel: 'Current Status',
      startDateLabel: 'Start Date',
      endDateLabel: 'End Date',
      statsEmployees: 'Participants',
      statsKpi: 'KPI Evaluation',
      statsE360: '360 Evaluation',
      statsEmpty: 'No statistics available',
      shortcutKpi: 'KPI Assessment',
      shortcutE360: '360 Evaluation',
      shortcutCalibration: 'Performance Calibration',
    },
    table: {
      name: 'Cycle Name',
      dateRange: 'Date Range',
      actions: 'Actions',
    },
    parentCycle: 'Parent Cycle',
    childCycles: 'Sub-cycles',
    noParent: 'None (Top-level)',
    selectParentCycle: 'Select Parent Cycle',
    createSubtitle: 'Set basic cycle information and key dates',
    createBasicTitle: 'Basic Information',
    createDateTitle: 'Cycle Dates',
    milestonesTitle: 'Milestones',
    milestonesHint: 'Optional, used to set deadlines for each phase',
    listSubtitle: 'Manage the full lifecycle of performance cycles',
    searchPlaceholder: 'Search cycle name...',
    filterStatusAll: 'All Statuses',
    filterTypeAll: 'All Types',
    empty: 'No cycles found',
    emptyCreate: 'Create the first cycle',
    progressLabel: 'Cycle Progress',
    pagePrev: 'Previous',
    pageNext: 'Next',
    pageLabel: 'Page',
    gradeConfig: 'Grade Configuration',
    gradeConfigDefault: 'Use default configuration',
    gradeConfigDefaultSuffix: '(Default)',
    placeholders: {
      name: 'e.g., 2026 Q1 Performance Cycle',
    },
    validation: {
      nameRequired: 'Please enter a cycle name',
      startDateRequired: 'Please select a start date',
      endDateRequired: 'Please select an end date',
      endDateAfterStart: 'End date must be after start date',
    },
    toastLoadFailed: 'Failed to load',
    toastActionFailed: 'Action failed',
    toastPublished: 'Cycle published',
    toastExecutionStarted: 'Execution started',
    toastEvaluationStarted: 'Evaluation started',
    toastCalibrationStarted: 'Calibration started',
    toastCompleted: 'Cycle completed',
    toastArchived: 'Cycle archived',
    toastDeleted: 'Cycle deleted',
    toastCreated: 'Cycle created',
    toastCreateFailed: 'Creation failed. Please try again.',
    confirmDelete: 'Are you sure you want to delete this cycle? This action cannot be undone.',
    confirmDeleteTitle: 'Delete Performance Cycle',
    confirmDeleteWithData: 'Deleting this cycle will also remove all associated evaluation data, KPI assignments, and other records. This action cannot be undone.',
  },

  // KPI management
  kpi: {
    title: 'KPI Assessment',
    my: 'My KPI',
    subtitle: 'Review and assess personal KPI performance',
    evaluation: 'KPI Evaluation',
    evaluationSubtitle: 'Evaluate and confirm team KPI assessments',
    evaluateSubordinates: 'Evaluate Team',
    teamKpi: 'Team KPI',
    selectCycle: 'Select Cycle',
    statusAll: 'All Statuses',
    pendingEvalLabel: 'Pending Evaluation',
    pendingConfirmLabel: 'Pending Confirmation',
    confirmedLabel: 'Confirmed',
    indicator: 'Indicator',
    score: 'Score',
    selfScore: 'Self Score',
    managerScore: 'Manager Score',
    finalScore: 'Final Score',
    targetValue: 'Target Value',
    actualValue: 'Actual Value',
    targetValueLabel: 'Target Value',
    selfScoreLabel: 'Self Score',
    managerScoreLabel: 'Manager Score',
    finalScoreLabel: 'Final Score',
    selfCommentLabel: 'Self Comment',
    managerCommentLabel: 'Manager Comment',
    stats: {
      total: 'Total Indicators',
      pendingSelfEval: 'Pending Self Review',
      pendingEval: 'Pending Evaluation',
      pendingConfirm: 'Pending Confirmation',
      confirmed: 'Confirmed',
      avgScore: 'Average Score',
    },
    emptyData: 'No KPI data available',
    emptyTeam: 'No pending KPI evaluations',
    unnamedIndicator: 'Unnamed Indicator',
    weightLabel: 'Weight {weight}%',
    userIndicatorCount: '{count} indicators',
    userPendingCount: '{count} pending',
    managerEvaluateAction: 'Evaluate',
    unknownUser: 'Unknown User',
    selfEval: {
      title: 'KPI Self Review',
      scoreLabel: 'Self Score',
      scorePlaceholder: '0-100',
      scoreHint: 'Enter a score between 0 and 100',
      commentLabel: 'Self Comment',
      commentPlaceholder: 'Describe evidence and completion status...',
      submit: 'Submit Self Review',
      submitting: 'Submitting...',
    },
    managerEval: {
      title: 'Manager Evaluation',
      employeeLabel: 'Employee',
      scoreLabel: 'Evaluation Score',
      scorePlaceholder: '0-100',
      commentLabel: 'Evaluation Comment',
      commentPlaceholder: 'Enter evaluation comments...',
      submit: 'Submit Evaluation',
      submitting: 'Submitting...',
      pendingHint: 'Pending manager evaluation',
    },
    table: {
      indicator: 'Indicator',
      weight: 'Weight',
      target: 'Target Value',
      baseTarget: 'Base Target',
      stretchTarget: 'Stretch Target',
      annualSplit: 'Annual/Quarter Targets',
      annualTargetPlaceholder: 'Annual target',
      annualSplitAction: 'Split Annual Target',
      splitting: 'Splitting...',
      selfEvaluation: 'Self Evaluation',
      managerEvaluation: 'Manager Evaluation',
      employee: 'Employee',
    },
    quarterSummary: 'Quarter total {sum} / Annual target {annual}',
    createIndicator: 'Create Indicator',
    createIndicatorTitle: 'Create Indicator',
    editIndicatorTitle: 'Edit Indicator',
    searchIndicatorPlaceholder: 'Search indicator name...',
    allCategories: 'All Categories',
    emptyIndicators: 'No indicators',
    emptyIndicatorCreate: 'Create the first indicator',
    indicatorUnitBadge: 'Unit: {unit}',
    indicatorTargetBadge: 'Target: {target}',
    indicatorRangeLabel: 'Score Range: {min} - {max}',
    indicatorNameLabel: 'Indicator Name',
    indicatorNamePlaceholder: 'Enter indicator name',
    indicatorDescLabel: 'Description',
    indicatorDescPlaceholder: 'Enter indicator description',
    indicatorCategoryLabel: 'Category',
    indicatorCategoryPlaceholder: 'e.g. Sales, Engineering',
    indicatorUnitLabel: 'Unit',
    indicatorUnitPlaceholder: 'e.g. %, units, USD',
    indicatorTargetValueLabel: 'Target Value',
    indicatorMinScoreLabel: 'Min Score',
    indicatorMaxScoreLabel: 'Max Score',
    actions: {
      selfEvaluate: 'Self Evaluate',
      managerEvaluate: 'Manager Evaluate',
      confirm: 'Confirm',
    },
    validation: {
      scoreRange: 'Score must be between 0 and 100',
      indicatorNameRequired: 'Please enter an indicator name',
      annualTargetRequired: 'Please enter an annual target value',
      quarterRequired: 'Please fill in all quarter targets',
      quarterSumMismatch: 'Quarter total must equal annual target',
    },
    confirmDeleteIndicator: 'Are you sure you want to delete this indicator? This action cannot be undone.',
    toastLoadFailed: 'Failed to load',
    toastActionFailed: 'Action failed',
    toastSelfSubmitted: 'Self review submitted',
    toastManagerSubmitted: 'Evaluation submitted',
    toastConfirmed: 'Assessment confirmed',
    toastIndicatorUpdated: 'Indicator updated',
    toastIndicatorCreated: 'Indicator created',
    toastIndicatorDeleted: 'Indicator deleted',
    toastQuarterUpdated: 'Quarter targets saved',
    toastQuarterUpdateFailed: 'Failed to save quarter targets',
    statuses: {
      PENDING: 'Pending',
      SELF_EVALUATED: 'Self Evaluated',
      MANAGER_EVALUATED: 'Manager Evaluated',
      CONFIRMED: 'Confirmed',
    },
    // KPI Assignment approval status
    assignmentStatus: {
      DRAFT: 'Draft',
      SUBMITTED: 'Pending Approval',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      PENDING_DEPENDENCY: 'Pending Confirmation',
      CONFIRMED: 'Confirmed',
    },
    // Approval related
    approval: {
      approve: 'Approve',
      reject: 'Reject',
      rejectReason: 'Rejection Reason',
      rejectReasonPlaceholder: 'Enter rejection reason...',
      rejectReasonRequired: 'Please provide a rejection reason',
      approveConfirm: 'Approve this KPI target?',
      rejectConfirm: 'Reject this KPI target?',
      approveAll: 'Approve All',
      approveAllConfirm: 'Approve all KPI targets for this employee?',
      goalApproval: 'Goal Approval',
      goalApprovalDesc: 'Review KPI targets submitted by your team',
      noSubmitted: 'No KPI targets pending approval',
      noSubmittedDesc: 'Submitted KPI targets will appear here',
      toastApproved: 'KPI target approved',
      toastRejected: 'KPI target rejected',
      toastApproveAllSuccess: 'All KPI targets approved',
      toastApproveFailed: 'Approval failed',
      toastRejectFailed: 'Rejection failed',
      rejectedBanner: 'This KPI was rejected by your manager. Please revise and resubmit.',
      rejectionReasonLabel: 'Rejection Reason',
      teamOverview: 'Team Overview',
      teamMembers: 'members',
      membersSubmitted: 'Submitted',
      membersDrafting: 'Drafting',
      membersNotStarted: 'Not Started',
    },
    // Dependency confirmation
    dependency: {
      title: 'Pending Alignment',
      desc: 'The following KPIs list you as a dependent. Please confirm alignment.',
      confirmAlign: 'Confirm Alignment',
      confirming: 'Confirming...',
      fromEmployee: 'From',
      fromEmployeeHint: 'Requested your collaboration on the KPI below',
      toastConfirmed: 'Alignment confirmed',
      toastConfirmFailed: 'Confirmation failed',
      noPending: 'No pending alignment confirmations',
      dialogTitle: 'Confirm Dependency Alignment',
      selectMyKpi: 'Select my KPI to bind',
      sourceKpi: 'Source KPI',
      noBinding: 'Skip binding, confirm directly',
      confirm: 'Confirm',
      cancel: 'Cancel',
      reject: 'Reject',
      rejectDialogTitle: 'Reject Dependency',
      rejectReasonLabel: 'Please provide a reason for rejection',
      rejectReasonPlaceholder: 'Explain the reason for rejection...',
      toastRejected: 'Dependency rejected',
      toastRejectFailed: 'Failed to reject',
      descriptionPlaceholder: 'Describe your requirement for the collaborator...',
      descriptionLabel: 'Requirement',
      rejectionReasonLabel: 'Rejection reason',
      pending: 'Pending',
      confirmed: 'Confirmed',
      rejected: 'Rejected',
      removeDialogTitle: 'Remove Dependency',
      removeDialogDesc: 'Are you sure you want to remove the dependency on {name}? The requirement description will also be cleared.',
      removeConfirm: 'Confirm Removal',
    },
    // Phase hints
    phase: {
      goalSetting: {
        title: 'Goal Setting Phase',
        desc: 'Please confirm your KPI targets and complete quarterly breakdown',
        submitAll: 'Submit Targets',
        submitting: 'Submitting...',
        toastSubmitted: 'KPI targets submitted',
        toastSubmitFailed: 'Failed to submit targets',
      },
      inProgress: {
        title: 'Execution Phase',
        desc: 'Currently in execution phase, please proceed with your KPI targets',
      },
      evaluating: {
        title: 'Evaluation Phase',
        desc: 'Please complete your KPI self-evaluation',
      },
      calibrating: {
        title: 'Calibration Phase',
        desc: 'Evaluation results are being calibrated. Final results may be adjusted.',
      },
      readonly: {
        title: 'Current Cycle',
        desc: 'View KPI targets and evaluation results',
      },
    },
    cycleDraft: {
      title: 'Cycle Not Published Yet',
      desc: 'This cycle is still in draft. Please wait for HR to publish it before setting your KPI targets.',
    },
    empty: 'No KPI Yet',
    emptyDesc: 'Click the button below to add your KPI',
    // Free-form add KPI
    addKpi: 'Add KPI',
    addKpiTitle: 'Add KPI',
    confirmAdd: 'Confirm',
    toastKpiAdded: 'KPI added successfully',
    toastAddKpiFailed: 'Failed to add KPI',
    indicatorsEmptyTitle: 'No indicators yet',
    indicatorsEmptyDesc: 'Create your first KPI indicator',
    indicatorsEmptyAction: 'Create first indicator',
    evaluationEmptyTitle: 'No KPI pending review',
    evaluationEmptyDesc: 'All KPI evaluations are complete',

    // /performance/kpi page-specific copy
    page: {
      indicatorNamePlaceholder: 'Enter KPI name',
      childNamePlaceholder: '{quarter} task name',
      addQuarterTask: 'Add quarter task',
      saveTargets: 'Save',
      quarterTargetsLabel: 'Quarter targets',
      autoSplit: 'Auto split',
      annualTargetLabel: 'Annual target: {value}',
      scoreUnit: 'pts',
      notSubmitted: 'Not submitted',
      pendingManagerEvaluation: 'Pending evaluation',
      needSelfEvaluationFirst: 'Complete self-evaluation first',
      showGuideTitle: 'Show guide',
      deleteKpiAriaLabel: 'Delete KPI',
      deleteChildAriaLabel: 'Delete child task',
      baseTargetPlaceholder: 'Enter base target (e.g., "Sign 30 customers")',
      stretchTargetPlaceholder: 'Enter stretch target (e.g., "Sign 50 customers")',
      draftNamePlaceholder: 'Enter KPI name (e.g., "Sign 50 new customers in Q1")',
      weightLabel: 'Weight',
      deleteDraftAriaLabel: 'Delete draft',
      draftHint: 'Save when ready, or add more KPIs',
      saveDraft: 'Save',
      saveAllDrafts: 'Save all',
      cancelDrafts: 'Cancel',
      pendingSaveCount: '{count} pending',
      submitDisabledHint: 'Please save all drafts first',
      submitDisabledHintWeight: 'Total weight is {weight}%. Please adjust to 100% before submitting.',
      submitDisabledHintTargets: 'Please generate Q1-Q4 (4 child KPIs) for each annual KPI and ensure child weights sum to 100% under each annual KPI.',
      submitDisabledHintIncomplete: 'Please ensure all KPIs are fully filled in and dependencies are confirmed',
      totalWeightLabel: 'Total weight {weight}%',
      weightWarning: 'Total KPI weight is {weight}%. It must be 100%.',
      guideButton: 'Guide',
      toastQuarterCreated: 'Q1-Q4 child KPIs created',
      toastQuarterCreateFailed: 'Failed to create quarter KPIs',
      toastQuarterAlreadyExists: 'Quarter KPIs already exist for this annual KPI',
      toastDependentUserSet: 'Dependent user set: {name}',
      toastDependentUserRemoved: 'Dependent user removed',
      toastDependentUserFailed: 'Failed to update dependent user',
      toastUpdateDescriptionFailed: 'Failed to update description',
      toastUpdateNameFailed: 'Failed to update name',
      toastUpdateWeightFailed: 'Failed to update weight',
      toastRowSaved: 'Saved',
      toastDeletedKpi: 'KPI deleted',
      toastDeleteFailed: 'Delete failed',
      toastAddChildFailed: 'Failed to add child task',
      toastUpdateChildNameFailed: 'Failed to update child name',
      toastUpdateChildWeightFailed: 'Failed to update child weight',
      toastDeletedChild: 'Child task deleted',
      toastDeleteChildFailed: 'Failed to delete child task',
      validation: {
        weightRequired: 'Please enter weight',
        weightRange: 'Weight must be between 1 and 100',
      },

      table: {
        name: 'KPI Name',
        weight: 'Weight',
        baseTarget: 'Base Target',
        stretchTarget: 'Stretch Target',
        dependentUser: 'Dependent',
        dependentKpi: 'Dependent KPI',
        status: 'Status',
        actions: 'Actions',
        annual: 'Annual',
        expand: 'Expand',
        collapse: 'Collapse',
        addQuarter: 'Generate Q1-Q4',
        creatingQuarter: 'Creating...',
        delete: 'Delete',
        decomposeTitle: 'Quarter KPIs (parent-child)',
        decomposeHint: '{count}/4 child KPIs created, child weight sum {sum}%',
        decomposeMissing: 'Please generate complete Q1-Q4 (4 child KPIs)',
        evaluation: 'Evaluation',
        save: 'Save',
        saving: 'Saving...',
        remove: 'Remove',
      },
    },

    // Inline @ mention picker
    mentionPicker: {
      removeTitle: 'Click to remove',
      cancel: 'Cancel',
      selectUser: '@Pick',
      searchPlaceholder: 'Search name',
      notFound: 'Not found',
      typeToSearch: 'Type to search',
    },

    // Right-side writing guide
    guide: {
      tabs: {
        guide: 'Guide',
        examples: 'Examples',
      },
      badLabel: 'Bad',
      goodLabel: 'Good',
      weightLabel: 'Weight {weight}%',
      suggestions: {
        title: 'Suggestions',
        count: '{count} suggestions',
        tooShort: '"{name}" is too short. Make it more specific.',
        missingNumber: '"{name}" lacks a measurable number. Add concrete metrics.',
        missingDesc: '"{name}" should include how to measure success or acceptance criteria.',
      },
      howToTitle: 'How to write good KPIs?',
      sections: [
        { title: 'Be specific and directional', bad: 'Improve English', good: 'In 6 months, give a domain talk in English' },
        { title: 'Make it challenging', bad: 'In 10 years, give a domain talk in English', good: 'In 6 months, give a domain talk in English' },
        { title: 'Describe KPIs quantitatively or qualitatively', bad: 'Memorize terms', good: 'Use 200 domain terms fluently' },
        { title: 'KPIs should be measurable and verifiable', bad: 'Make PPT in English', good: 'Make PPT in English and explain domain concepts accurately' },
      ],
      principlesTitle: 'Other principles',
      principles: [
        'Keep 3-7 KPIs per person',
        'Total weight should be 100%',
        'Avoid vague items like "Project X", "Other", "Key projects"',
        'Describe the outcome state, not just tasks',
      ],
      exampleGroups: [
        {
          title: 'Sales',
          items: [
            { name: 'Sign 50 new customers in Q1', desc: 'Acquire 50 new enterprise customers via online and onsite visits; contract value >= 50k each', weight: 30 },
            { name: 'Reach 95% quarterly collection rate', desc: 'Ensure customers pay on time; overdue rate <= 5%', weight: 20 },
          ],
        },
        {
          title: 'Engineering',
          items: [
            { name: 'Core system availability at 99.9%', desc: 'Improve architecture and monitoring; uptime > 99.9%; <= 2 P0 incidents', weight: 25 },
            { name: '100% code review coverage', desc: 'Every merge requires at least 1 reviewer; resolve feedback within 48 hours', weight: 15 },
          ],
        },
        {
          title: 'Management',
          items: [
            { name: 'Team satisfaction >= 4.2', desc: 'Regular 1:1s and career talks; quarterly anonymous survey >= 4.2/5.0', weight: 20 },
            { name: '80% critical role bench strength', desc: 'Develop at least 1 backup for each critical role; create and review IDPs', weight: 15 },
          ],
        },
      ],
    },
  },

  // 360 evaluation
  e360: {
    title: '360 Evaluation',
    subtitle: 'Manage 360-degree evaluations',
    my: 'My Evaluation',
    feedback: 'Feedback Tasks',
    evaluators: 'Evaluators',
    relationship: 'Relationship',
    anonymity: 'Anonymity Level',
    addEvaluator: 'Add Evaluator',
    pendingTasks: 'Pending Evaluations',
    create: 'Create Evaluation',
    filterCycle: 'Select Cycle',
    statusAll: 'All Statuses',
    statsTotal: 'Total Evaluations',
    empty: 'No 360 evaluations available',
    emptyCreate: 'Create the first evaluation',
    cardTitle: '360 Evaluation',
    evaluateeLabel: 'Evaluatee',
    progressCompleted: 'Completed',
    modalTitle: 'Create 360 Evaluation',
    modalTitleLabel: 'Evaluation Title',
    modalTitlePlaceholder: 'Enter evaluation title',
    modalDescLabel: 'Description',
    modalDescPlaceholder: 'Enter evaluation description',
    modalEvaluateeLabel: 'Evaluatee',
    modalEvaluateePlaceholder: 'Enter evaluatee ID',
    modalEvaluateeHelper: 'TODO: Replace with user selector component',
    modalAnonymityLabel: 'Anonymity Level',
    anonymityNone: 'Fully Transparent',
    anonymityPartial: 'Partial (show relationship type)',
    anonymityFull: 'Fully Anonymous',
    toastLoadFailed: 'Failed to load',
    toastTitleRequired: 'Please enter an evaluation title',
    toastEvaluateeRequired: 'Please select an evaluatee',
    toastCreated: 'Evaluation created',
    toastActionFailed: 'Action failed',
    toastStarted: 'Evaluation started',
    toastCompleted: 'Evaluation completed',
    actions: {
      start: 'Start Evaluation',
      complete: 'Complete Collection',
      submit: 'Submit Feedback',
    },
    detail: {
      title: '360 Evaluation Details',
      notFound: 'Evaluation not found',
      infoTitle: 'Evaluation Info',
      evaluatorCountLabel: 'Evaluator Count',
      evaluatorCountValue: '{count} people',
      resultTitle: 'Evaluation Results',
      radarHint: 'Scores range from 1 to 5',
      radarEmpty: 'No dimension scores',
      dimensionScores: 'Dimension Scores',
      overallScore: 'Overall Score',
      evaluatorListTitle: 'Evaluators',
      completedSummary: '{completed}/{total} completed',
      emptyEvaluators: 'No evaluators',
      emptyAddEvaluator: 'Add the first evaluator',
      unknownUser: 'Unknown User',
      taskCompleted: 'Completed',
      taskPending: 'Pending',
      progressTitle: 'Completion Progress',
      progressCompletedLabel: 'Completed',
      progressEmpty: 'Please add evaluators first',
      progressNoneCompleted: 'No evaluations completed yet',
      progressRemaining: '{count} remaining',
      progressAllCompleted: 'All evaluators completed',
      timelineTitle: 'Status Timeline',
      timelineCreated: 'Created',
      timelineStarted: 'Started',
      timelineCompleted: 'Completed',
      feedbackListTitle: 'Feedback List',
      feedbackListEmpty: 'No feedback available',
      addEvaluatorTitle: 'Add Evaluator',
      evaluatorLabel: 'Evaluator',
      evaluatorPlaceholder: 'Enter evaluator ID',
      evaluatorHelper: 'TODO: Replace with user selector',
      relationshipLabel: 'Relationship',
      evaluatorRequired: 'Please enter evaluator ID',
      evaluatorAdded: 'Evaluator added',
      evaluatorRemoved: 'Evaluator removed',
      confirmRemoveEvaluator: 'Are you sure you want to remove this evaluator?',
      completeAction: 'End Evaluation',
    },
    statuses: {
      DRAFT: 'Draft',
      IN_PROGRESS: 'In Progress',
      COMPLETED: 'Completed',
    },
    relationships: {
      SELF: 'Self',
      SUPERIOR: 'Superior',
      SUPERVISOR: 'Supervisor',
      PEER: 'Peer',
      SUBORDINATE: 'Subordinate',
      EXTERNAL: 'External',
    },
  },

  // 360 feedback
  e360Feedback: {
    title: 'Pending Evaluations',
    subtitle: 'Complete the 360 evaluation tasks assigned to you',
    anonymousTitle: 'Anonymous Evaluation Notice',
    anonymousDesc: 'Your feedback will be anonymized based on the evaluation settings.',
    anonymousHint: 'Your feedback will be anonymized',
    guideTitle: 'Evaluation Guidelines',
    guideConfidential: 'Your responses are confidential and anonymous. All feedback will be aggregated and reported without identifying individual contributors.',
    guideHonest: 'Please answer honestly and constructively based on your direct experience working with this leader. Your input is essential to supporting leadership growth and organizational effectiveness.',
    guideScaleTitle: 'Rating Scale (1–5)',
    guideScale1: '1 — Strongly Disagree',
    guideScale2: '2 — Disagree',
    guideScale3: '3 — Neutral / Acceptable',
    guideScale4: '4 — Agree',
    guideScale5: '5 — Strongly Agree / Role Model Performance',
    empty: 'No pending evaluation tasks',
    emptyHint: 'Tasks will appear here when colleagues invite you for 360 evaluation',
    pendingTitle: 'Pending',
    completedTitle: 'Completed',
    statusCompleted: 'Completed',
    statusOverdue: 'Overdue',
    statusCancelled: 'Cancelled',
    statusPending: 'Pending',
    evaluateeLabel: 'Evaluatee',
    scoreLabel: 'Score',
    scorePlaceholder: 'Select a score',
    relationshipLabel: 'Your Relationship',
    dueDateLabel: 'Due Date',
    dueDatePrefix: 'Due',
    submittedAtPrefix: 'Submitted',
    fillFeedback: 'Provide Feedback',
    evaluateTitle: 'Evaluate',
    anonymousBadge: 'Anonymous Evaluation',
    dimCommentPlaceholder: 'Brief comment (optional)',
    cancelAction: 'Cancel',
    submitAction: 'Submit Evaluation',
    modalTitle: 'Submit Evaluation Feedback',
    scoreLow: 'Needs Improvement',
    scoreHigh: 'Excellent',
    commentPlaceholder: 'Additional notes (optional)',
    overallLabel: 'Overall Comment',
    overallPlaceholder: 'Provide overall feedback and suggestions...',
    unknown: 'Unknown',
    unknownUser: 'Unknown User',
    tabTasks: 'My Tasks',
    tabResults: 'My Results',
    aggregateScore: 'Overall',
    respondentCount: '',
    feedbackSummary: 'Feedback Summary',
    noResultsHint: 'Results will appear here after 360 evaluators are configured during KPI self-assessment',
    toastLoadFailed: 'Failed to load',
    toastScoreRequired: 'Please score all dimensions',
    toastSubmitted: 'Feedback submitted',
    toastActionFailed: 'Action failed',
    dimensions: {
      communication: {
        name: 'Communication',
        desc: 'Ability to communicate and listen effectively',
      },
      teamwork: {
        name: 'Teamwork',
        desc: 'Ability to collaborate with team members',
      },
      leadership: {
        name: 'Leadership',
        desc: 'Ability to lead and motivate the team',
      },
      professional: {
        name: 'Professional Skills',
        desc: 'Professional knowledge and skill level',
      },
      innovation: {
        name: 'Innovation',
        desc: 'Ability to propose new ideas and solve problems',
      },
    },
  },

  // 360 Evaluation & KPI Integration
  e360Kpi: {
    sectionTitle: '360 Peer Review Setup',
    sectionDesc: 'Select colleagues to evaluate you. Requests will be sent after you submit your self-evaluation.',
    addEvaluator: 'Add Evaluator',
    selectUser: 'Select Colleague',
    selectRelationship: 'Select Relationship',
    noEvaluators: 'No evaluators added yet',
    addHint: 'Recommend 3-5 evaluators covering supervisors, peers, and subordinates',
    referenceTitle: '360 Evaluation Reference',
    aggregateScore: '360 Score',
    dimensionScores: 'Dimension Scores',
    evaluatorCount: 'Evaluators',
    pendingCount: 'Pending',
    completedCount: 'Completed',
    noResults: 'No 360 evaluation data available',
    viewDetail: 'View 360 Details',
    relationship: {
      SUPERVISOR: 'Supervisor',
      PEER: 'Peer',
      SUBORDINATE: 'Subordinate',
    },
  },

  // Calibration
  calibration: {
    title: 'Calibration',
    subtitle: 'Review score distribution, adjust grades, ensure fair and consistent evaluations',
    overview: {
      totalEmployees: 'Employees',
      avgScore: 'Average',
      medianScore: 'Median',
      persons: '',
    },
    distribution: 'Grade Distribution',
    distributionHint: 'Target line',
    targetActualLabel: 'Target | Actual',
    targetActualValue: '{target}% | {actual}%',
    targetLabel: 'Target: {target}%',
    ranking: {
      title: 'Employee Score Ranking',
      rank: 'Rank',
      employee: 'Employee',
      kpiCount: 'KPIs',
      selfScore: 'Self (weighted)',
      managerScore: 'Manager (weighted)',
      diff: 'Diff',
      grade: 'Grade',
      calibratedGrade: 'Calibrated Grade',
      calibrationScore: 'Calibrated Score',
      note: 'Note',
      action: 'Action',
      expand: 'Expand KPI details',
      collapse: 'Collapse',
      save: 'Save',
      saved: 'Saved',
    },
    kpiDetail: {
      indicator: 'Indicator',
      weight: 'Weight',
      selfScore: 'Self Score',
      managerScore: 'Manager Score',
      selfComment: 'Self Comment',
      managerComment: 'Manager Comment',
    },
    empty: 'No calibration data',
    emptyHint: 'Please select a cycle in calibrating phase',
    noCalibratingCycle: 'No cycles in calibrating phase',
    cycleLabel: 'Performance Cycle',
    cyclePlaceholder: 'Select a cycle',
    grades: {
      S: 'Outstanding',
      A: 'Excellent',
      B: 'Good',
      C: 'Needs Improvement',
      D: 'Unsatisfactory',
    },
    toastLoadFailed: 'Failed to load calibration data',
    toastSaved: 'Calibration saved',
    toastSaveFailed: 'Save failed',
    adjust: 'Adjust Grade',
    reason: 'Adjustment Reason',
    reasonPlaceholder: 'Explain the reason for this adjustment...',
    logs: 'Adjustment Logs',
    emptyEmployees: 'No employees',
    totalScoreLabel: 'Total Score',
    scoreLabel: 'Score',
    originalGradeLabel: 'Original Grade',
    currentGradeLabel: 'Current Grade',
    newGradeLabel: 'Adjusted Grade',
    notFound: 'Calibration session not found',
    historyTitle: 'Grade Adjustment History',
    historyEmpty: 'No adjustment records',
    adjustedByLabel: 'Adjusted by: {name}',
    createTitle: 'Create Calibration Session',
    cycleEmptyHint: 'No cycles in calibration stage. Please advance a cycle to calibration.',
    nameLabel: 'Session Name',
    namePlaceholder: 'e.g. 2024 Q1 Sales Calibration',
    descLabel: 'Session Description',
    descPlaceholder: 'Purpose and agenda...',
    scheduledLabel: 'Scheduled Time',
    completedLabel: 'Completed',
    participantsCount: '{count} participants',
    emptyCreate: 'Create the first session',
    confirmAdjustTitle: 'Confirm Grade Adjustment',
    filters: {
      allCycles: 'All Cycles',
      allStatuses: 'All Statuses',
    },
    actions: {
      create: 'Create Session',
      start: 'Start Calibration',
      complete: 'Complete Calibration',
    },
    table: {
      employee: 'Employee',
      score: 'Score',
      currentGrade: 'Current Grade',
      adjustGrade: 'Adjust Grade',
      reason: 'Adjustment Reason',
    },
    statuses: {
      SCHEDULED: 'Scheduled',
      IN_PROGRESS: 'In Progress',
      COMPLETED: 'Completed',
    },
    validation: {
      required: 'Please fill in required fields',
      adjustReasonRequired: 'Please provide an adjustment reason',
      adjustGradeRequired: 'Please select a different target grade',
    },
    toastCreated: 'Calibration session created',
    toastCreateFailed: 'Creation failed',
    toastStarted: 'Calibration session started',
    toastCompleted: 'Calibration session completed',
    toastDeleted: 'Calibration session deleted',
    toastAdjusted: 'Grade adjusted',
    toastAdjustFailed: 'Adjustment failed',
    toastActionFailed: 'Action failed',
    cycleLoadFailed: 'Failed to load cycles',
  },

  settings: {
    grades: {
      title: 'Grade Settings',
      subtitle: 'Manage performance grade configurations',
      createAction: 'New Configuration',
      listTitle: 'Configuration List',
      empty: 'No configurations',
      emptyCreate: 'Create the first configuration',
      defaultBadge: 'Default',
      detailCreateTitle: 'New Configuration',
      detailTitle: 'Configuration Details',
      detailEmptyTitle: 'Select a configuration',
      detailEmptyHint: 'Select a configuration on the left to view details',
      detailEmptyAction: 'Or click "New Configuration" to create a new grade system',
      nameLabel: 'Configuration Name',
      namePlaceholder: 'Enter configuration name',
      descLabel: 'Description',
      descPlaceholder: 'Configuration description (optional)',
      weightsLabel: 'Score Weights',
      selfEvalWeight: 'Self Evaluation',
      managerEvalWeight: 'Manager Evaluation',
      weightsSumError: 'Weights must sum to 100%',
      levelsLabel: 'Grade Levels',
      codePlaceholder: 'Code',
      levelNamePlaceholder: 'Name',
      minScorePlaceholder: 'Min',
      maxScorePlaceholder: 'Max',
      addLevelAction: '+ Add Grade',
      editAction: 'Edit Configuration',
      setDefaultAction: 'Set as Default',
      notesTitle: 'Configuration Notes',
      notes: {
        code: 'Grade codes should be a single uppercase letter (e.g. S, A, B, C, D)',
        range: 'Score ranges should be ordered high to low without overlap',
        defaultApply: 'Default configuration applies to newly created cycles',
        history: 'Changes do not affect completed cycles',
      },
      defaults: {
        S: { name: 'Outstanding', description: 'Exceeds expectations' },
        A: { name: 'Excellent', description: 'Delivers high-quality results' },
        B: { name: 'Good', description: 'Meets expectations' },
        C: { name: 'Needs Improvement', description: 'Partially meets goals' },
        D: { name: 'Unsatisfactory', description: 'Does not meet goals' },
      },
      validation: {
        nameRequired: 'Please enter a configuration name',
        gradeRequired: 'Please add at least one grade',
        gradeCodeRequired: 'Grade code and name are required',
        minScoreGreaterThanMax: 'Minimum score cannot exceed maximum for grade {code}',
      },
      toastLoadFailed: 'Failed to load',
      toastCreated: 'Configuration created',
      toastUpdated: 'Configuration updated',
      toastSaveFailed: 'Save failed',
      toastDeleteDefault: 'Default configuration cannot be deleted',
      confirmDelete: 'Are you sure you want to delete this configuration?',
      toastDeleted: 'Configuration deleted',
      toastDeleteFailed: 'Deletion failed',
      toastAlreadyDefault: 'Already the default configuration',
      toastSetDefault: 'Set as default configuration',
      toastActionFailed: 'Action failed',
    },
    e360Templates: {
      title: '360 Templates',
      createAction: 'New Template',
      listTitle: 'Template List',
      empty: 'No templates',
      emptyCreate: 'Create the first template',
      defaultBadge: 'Default',
      detailCreateTitle: 'New Template',
      detailTitle: 'Template Details',
      detailEmptyTitle: 'Select a Template',
      detailEmptyHint: 'Select a template on the left to view details',
      nameLabel: 'Template Name',
      namePlaceholder: 'Enter template name',
      descLabel: 'Description',
      descPlaceholder: 'Template description (optional)',
      relationshipsLabel: 'Evaluation Relationships',
      dimensionsLabel: 'Evaluation Dimensions',
      dimNamePlaceholder: 'Dimension name, e.g. "Communication & Interpersonal Skills"',
      dimNameEnPlaceholder: 'Name (English)',
      dimDescPlaceholder: 'Dimension description — shown to evaluators during feedback',
      dimDescEnPlaceholder: 'Description (English)',
      addDimensionAction: '+ Add Dimension',
      editAction: 'Edit Template',
      setDefaultAction: 'Set as Default',
      notesTitle: 'Usage Notes',
      notes: {
        dimensions: 'Evaluation dimensions are shown in the 360 feedback form, each rated on a 1-5 scale',
        relationships: 'Relationship types determine which roles can participate in the evaluation',
        defaultApply: 'Default template applies to newly created 360 evaluations',
        anonymous: 'All 360 feedback is anonymous and results are aggregated by relationship type',
      },
      validation: {
        nameRequired: 'Please enter a template name',
        dimensionRequired: 'Please add at least one evaluation dimension',
        relationshipRequired: 'Please select at least one relationship type',
      },
      toastLoadFailed: 'Failed to load',
      toastCreated: 'Template created',
      toastUpdated: 'Template updated',
      toastSaveFailed: 'Failed to save',
      toastDeleteDefault: 'Cannot delete default template',
      confirmDelete: 'Are you sure you want to delete this template?',
      toastDeleted: 'Template deleted',
      toastDeleteFailed: 'Failed to delete',
      toastSetDefault: 'Set as default template',
      toastActionFailed: 'Action failed',
    },
  },

  // Results
  result: {
    title: 'Performance Results',
    my: 'My Performance',
    admin: 'Results Management',
    grade: 'Performance Grade',
    totalScore: 'Total Score',
    publish: 'Publish Results',
    export: 'Export Report',
    viewed: 'Viewed',
    notViewed: 'Not Viewed',
    pageTitle: 'My Performance Results',
    pageSubtitle: 'View your historical performance results',
    emptyTitle: 'No performance results',
    emptyDesc: 'Results will appear here after publication',
    publishedAt: 'Published on',
    unpublished: 'Unpublished',
    pending: 'Pending',
    scoreUnit: 'pts',
    trendUp: 'Up +',
    trendDown: 'Down ',
    trendSame: 'No change',
    kpiScore: 'KPI Score',
    e360Score: '360 Score',
    remarks: 'Remarks',
    trendTitle: 'Performance Trend',
    detailTitle: 'Result Details',
    gradeDescriptionLabel: 'Grade Description',
    gradeDescriptionEmpty: 'No grade description',
    scoreBreakdownTitle: 'Score Breakdown',
    scoreBreakdownIndicator: 'Indicator',
    scoreBreakdownScore: 'Score',
    scoreBreakdownWeight: 'Weight',
    scoreBreakdownEmpty: 'No score breakdown available',
    selectHint: 'Select a cycle on the right to view results',
    historyTitle: 'History',
    noteTitle: 'Result Notes',
    noteDesc: 'Results are calculated based on KPI and 360 evaluations. Contact your manager or HR for questions.',
    toastLoadFailed: 'Failed to load',
  },

  // Results admin
  resultsAdmin: {
    title: 'Results Management',
    subtitle: 'Calculate, publish, and manage results',
    calculate: 'Calculate Results',
    publishAll: 'Publish All',
    publishSelected: 'Publish Selected',
    export: 'Export',
    filterCycle: 'Select Cycle',
    filterGradeAll: 'All Grades',
    filterStatusAll: 'All Statuses',
    statusPublished: 'Published',
    statusUnpublished: 'Unpublished',
    statusViewed: 'Viewed',
    statsTotal: 'Total',
    statsCalculated: 'Calculated',
    statsPublished: 'Published',
    statsViewed: 'Viewed',
    selectCycleHint: 'Select a cycle to view results',
    emptyResults: 'No results available',
    tableEmployee: 'Employee',
    tableScore: 'Total Score',
    tableGrade: 'Grade',
    tableStatus: 'Status',
    pagePrev: 'Previous',
    pageNext: 'Next',
    gradeDistributionTitle: 'Grade Distribution',
    noData: 'No Data',
    avgScore: 'Average Score',
    exportFilePrefix: 'Performance_Results',
    toastLoadFailed: 'Failed to load',
    toastSelectCycle: 'Please select a cycle',
    toastCalculated: 'Results calculated',
    toastCalculateFailed: 'Calculation failed',
    toastPublished: 'Results published',
    toastPublishFailed: 'Publish failed',
    toastExported: 'Export successful',
    toastExportFailed: 'Export failed',
    logLoadCyclesFailed: 'Failed to load cycles',
    statsConfirmed: 'Confirmed',
    statsAppealed: 'Appealed',
    confirmStatusConfirmed: 'Confirmed',
    confirmStatusAppealed: 'Appealed',
    confirmStatusPending: 'Pending',
    confirmStatusResolved: 'Resolved',
    appealReasonLabel: 'Appeal Reason',
  },

  // Result confirmation
  resultConfirm: {
    title: 'Result Confirmation',
    pendingHint: 'Please review and confirm your performance result',
    confirmDesc: 'Please review and confirm your performance evaluation result',
    confirmBtn: 'Confirm',
    appealBtn: 'Appeal',
    confirmedLabel: 'Confirmed',
    confirmedAt: 'Confirmed on',
    appealedLabel: 'Appeal Submitted',
    appealedAt: 'Appealed on',
    appealResolvedLabel: 'Appeal Resolved',
    appealModalTitle: 'Performance Appeal',
    appealModalDesc: 'Please describe your objection. HR will review your appeal after submission.',
    appealReasonPlaceholder: 'Describe your reasons for disagreeing with the evaluation result...',
    appealReasonRequired: 'Please provide an appeal reason',
    appealSubmit: 'Submit Appeal',
    appealCancel: 'Cancel',
    toastConfirmed: 'Result confirmed',
    toastAppealed: 'Appeal submitted',
    toastFailed: 'Action failed',
    recentResult: 'Recent Result',
    noResult: 'No published results yet',
    viewDetail: 'View Details',
    scoreUnit: 'pts',
    trendSame: 'Flat',
    resultTip: 'Performance results are published after manager evaluation and calibration. Click "View Details" to see KPI scores, 360 feedback, and more.',
    detailTitle: 'Performance Result Confirmation',
    overallResult: 'Overall Result',
    kpiBreakdown: 'KPI Score Breakdown',
    calibrationNote: 'Calibration Adjustment',
    calibrationDesc: 'Your score has been adjusted through management calibration',
    originalScoreLabel: 'Original Score',
    calibratedScoreLabel: 'Calibrated Score',
    selfScoreLabel: 'Self',
    managerScoreLabel: 'Manager',
    finalScoreLabel: 'Calibrated',
    weightLabel: 'Weight',
    commentLabel: 'Comments',
    noComment: 'No comments',
    e360Summary: '360 Evaluation',
    e360Desc: '360 evaluation is for reference only and not included in the final score',
    backToOverview: 'Back to Overview',
    alreadyConfirmed: 'You confirmed this result on {date}',
    alreadyAppealed: 'You appealed on {date}',
    collapseKpiDetails: 'Collapse KPI Details',
    expandKpiDetails: 'View KPI Details',
  },

  // Grade configuration
  grade: {
    title: 'Grade Configuration',
    name: 'Grade Name',
    code: 'Grade Code',
    minScore: 'Min Score',
    maxScore: 'Max Score',
    color: 'Color',
    default: 'Default Configuration',
    setDefault: 'Set as Default',
  },

  // Statistics
  stats: {
    kpiCompletion: 'KPI Target Completion',
    kpiEvaluation: 'KPI Evaluation',
    e360Tasks: '360 Tasks',
    e360Completion: '360 Completion Rate',
    gradeDistribution: 'Grade Distribution',
  },

  // Analytics
  analytics: {
    title: 'Analytics',
    subtitle: 'View performance statistics and analysis reports',
    filterTitle: 'Report Filters',
    noData: 'No Data',
    selectCycle: 'Please Select a Cycle',
    selectCyclePlaceholder: 'Select Cycle',
    departmentAll: 'All Departments',
    tabPersonal: 'Personal Report',
    tabTeam: 'Team Report',
    tabCompany: 'Company Report',
    kpiIndicators: 'KPI Indicators',
    selfEvaluated: 'Self Evaluated',
    managerEvaluated: 'Manager Evaluated',
    avgScore: 'Average Score',
    evaluation360Tasks: '360 Evaluation Tasks',
    pending: 'Pending',
    completed: 'Completed',
    trendTitle: 'Performance Trend',
    scoreUnit: 'pts',
    latestScore: 'Latest Score',
    changeFromLast: 'Change vs Last',
    trendHighestScore: 'Highest',
    averageScore: 'Average Score',
    teamSize: 'Team Size',
    kpiAverage: 'KPI Average',
    highLowScore: 'High/Low',
    gradeDistribution: 'Grade Distribution',
    totalEmployees: 'Participants',
    highestScore: 'Highest Score',
    lowestScore: 'Lowest Score',
    averagesTitle: 'Average Scores',
    kpiLabel: 'KPI',
    e360Label: '360',
    completionTitle: 'Completion Stats',
    kpiCompletionRate: 'KPI Evaluation Rate',
    e360CompletionRate: '360 Completion Rate',
    departmentRanking: 'Department Ranking',
    employeeCountUnit: 'employees',
    tabDepartment: 'Department Report',
    selectDepartment: 'Select Department',
    selectDepartmentHint: 'Please select a department to view report',
    deptEmployeeCount: 'Headcount',
    scoreDistribution: 'Score Distribution',
    evalProgress: 'Evaluation Progress',
    kpiEval: 'KPI Evaluation',
    e360Eval: '360 Evaluation',
    kpiIndicatorCount: '{count} indicators',
    e360CompletedCount: '{completed}/{total} completed',
    kpiEvaluatedCount: '{evaluated}/{total} evaluated',
    kpiAvg: 'KPI Avg',
    e360Avg: '360 Avg',
    topDept: 'Top Dept',
    bottomDept: 'Bottom Dept',
  },

  // Strategic Objectives
  strategic: {
    title: 'Strategic Objectives',
    subtitle: 'Define organizational strategic objectives and assign to people',
    create: 'Create Objective',
    createTitle: 'Create Strategic Objective',
    editTitle: 'Edit Strategic Objective',
    nameLabel: 'Objective Name',
    namePlaceholder: 'Enter strategic objective name',
    descLabel: 'Description',
    descPlaceholder: 'Enter description (optional)',
    seqLabel: 'Seq',
    departmentLabel: 'Department',
    selectDepartment: 'Select department',
    searchDepartment: 'Search department...',
    noDepartments: 'No departments found',
    selectAssignees: 'Select assignees',
    selectCycle: 'Select Cycle',
    selectCycleHint: 'Please select a performance cycle first',
    empty: 'No strategic objectives',
    emptyCreate: 'Create first strategic objective',
    assignees: 'Assignees',
    noAssignees: 'None',
    addAssignee: 'Add',
    assigneePlaceholder: 'Enter user IDs (comma separated)',
    confirmDeleteTitle: 'Delete Strategic Objective',
    confirmDeleteDesc: 'Are you sure you want to delete this objective? Associated assignments will also be removed.',
    validation: {
      nameRequired: 'Please enter objective name',
    },
    toastLoadFailed: 'Load failed',
    toastCreated: 'Objective created',
    toastUpdated: 'Objective updated',
    toastDeleted: 'Objective deleted',
    toastAssigned: 'Assignees updated',
    toastActionFailed: 'Action failed',
    actionsColumn: 'Actions',
    searchNoMatch: 'No matching users',
    searchHint: 'Type to search',
  },

  // Common
  common: {
    viewDetail: 'View Detail',
    viewAll: 'View All',
    submit: 'Submit',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    back: 'Back',
    required: 'Required',
    optional: 'Optional',
    comment: 'Comment',
    attachment: 'Attachment',
    noData: 'No Data',
  },
};
